import { useState, type ReactNode } from 'react'
import {
  Activity,
  BarChart3,
  BriefcaseBusiness,
  ClipboardList,
  Globe2,
  Heart,
  Timer,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  DashboardMetricCard,
  DashboardMetricGrid,
  PageContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@yeshuman/sdk'

import {
  usePlatformPulse,
  type PlatformPulsePeriod,
  type PlatformPulseReport,
} from '../../hooks/use-platform-pulse'

const NOTE_PREVIEW_CHARS = 80

function TruncatedNote({ note }: { note: string }) {
  const [expanded, setExpanded] = useState(false)
  const trimmed = note.trim()
  if (!trimmed) {
    return <span className="text-muted-foreground">—</span>
  }
  if (trimmed.length <= NOTE_PREVIEW_CHARS) {
    return <span className="whitespace-pre-wrap">{trimmed}</span>
  }
  return (
    <span className="block max-w-xs">
      <span className="whitespace-pre-wrap">
        {expanded ? trimmed : `${trimmed.slice(0, NOTE_PREVIEW_CHARS).trimEnd()}…`}
      </span>{' '}
      <button
        type="button"
        className="text-primary underline-offset-4 hover:underline"
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? 'Show less' : 'Expand'}
      </button>
    </span>
  )
}

function providerBreakdown(byProvider: Record<string, number>): string {
  const parts = Object.entries(byProvider)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => `${name} ${count}`)
  return parts.length ? parts.join(' · ') : 'No boards yet'
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`
}

function formatDays(value: number | null): string {
  if (value == null) return '—'
  return `${value.toFixed(1)}d`
}

function Section({
  title,
  description,
  children,
  link,
}: {
  title: string
  description: string
  children: ReactNode
  link?: { to: string; label: string }
}) {
  return (
    <section className="space-y-3 border-t border-border/60 pt-8 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>
        </div>
        {link ? (
          <Link
            to={link.to}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {link.label}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function PulseStatus({
  isLoading,
  error,
  children,
}: {
  isLoading: boolean
  error: Error | null
  children: ReactNode
}) {
  const status = (error as (Error & { status?: number }) | null)?.status
  if (status === 401 || status === 403) {
    return (
      <p className="text-sm text-muted-foreground">
        Platform operators only. Switch to the platform focus to view Platform pulse.
      </p>
    )
  }
  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>
  }
  if (isLoading && !children) {
    return <p className="text-sm text-muted-foreground">Loading pulse…</p>
  }
  return <>{children}</>
}

function DomainHighlights({ data }: { data: PlatformPulseReport['skill_pool'] }) {
  if (data.empty_catalog) {
    return (
      <p className="text-sm text-muted-foreground">
        {data.message || 'Publish skills on Skill Lexicon to see pool coverage.'}{' '}
        <Link to={data.lexicon_path} className="text-primary underline-offset-4 hover:underline">
          Open Skill Lexicon
        </Link>
      </p>
    )
  }
  if (data.top_domains.length === 0) {
    return <p className="text-sm text-muted-foreground">No published domains yet.</p>
  }
  const max = Math.max(1, ...data.top_domains.map((domain) => domain.candidate_count))
  return (
    <ul className="space-y-2">
      {data.top_domains.map((domain) => (
        <li key={domain.id} className="flex items-center gap-3">
          <span className="w-36 shrink-0 truncate text-sm">{domain.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
            <div
              className="h-2 rounded bg-primary"
              style={{ width: `${(domain.candidate_count / max) * 100}%` }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-sm tabular-nums">
            {domain.candidate_count}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function PlatformOpsPulsePage() {
  const [period, setPeriod] = useState<PlatformPulsePeriod>('7d')
  const { data, isLoading, error } = usePlatformPulse(period)

  return (
    <PageContainer className="max-w-5xl space-y-8">
      <header className="space-y-3">
        <p className="text-sm font-medium tracking-wide text-muted-foreground">TalentCo</p>
        <h1 className="text-3xl font-semibold tracking-tight">Platform pulse</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          What is really going on on the platform — skill pool, jobs in play, fulfillment,
          ingest, activity, and current rubric versions.
        </p>
        <div className="flex items-center gap-2 pt-1" role="group" aria-label="Period">
          {(['7d', '30d'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setPeriod(value)}
              className={
                period === value
                  ? 'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'
                  : 'rounded-md bg-muted px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'
              }
            >
              {value === '7d' ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
        </div>
      </header>

      <PulseStatus isLoading={isLoading} error={error as Error | null}>
        {data ? (
          <>
            <Section
              title="Skill pool"
              description="Published catalog × talent profiles (TC-420 highlights)."
              link={{ to: data.skill_pool.report_path, label: 'Full skill pool report' }}
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Coverage"
                  value={formatPercent(data.skill_pool.coverage_ratio)}
                  description={`${data.skill_pool.covered_profile_count} of ${data.skill_pool.profile_count} profiles`}
                  icon={BarChart3}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Catalog use"
                  value={formatPercent(data.skill_pool.catalog_use_ratio)}
                  description={`${data.skill_pool.possessed_published_skills} of ${data.skill_pool.published_skills} published`}
                  icon={BarChart3}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Concentration"
                  value={formatPercent(data.skill_pool.concentration_ratio)}
                  description="Top-10 share of possessions"
                  icon={BarChart3}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Depth"
                  value={formatPercent(data.skill_pool.depth_ratio)}
                  description="Advanced + Expert share"
                  icon={BarChart3}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
              <p className="text-sm text-muted-foreground">
                Uncovered profiles:{' '}
                <span className="font-medium tabular-nums">
                  {data.skill_pool.uncovered_profile_count}
                </span>
                {' · '}
                <Link
                  to={data.skill_pool.lexicon_path}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Skill Lexicon
                </Link>
              </p>
              <DomainHighlights data={data.skill_pool} />
            </Section>

            <Section
              title="Jobs in play"
              description="Managed publication status, marketplace listings, and stale published roles."
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Published managed"
                  value={String(data.jobs_in_play.managed.published)}
                  description={`Draft ${data.jobs_in_play.managed.draft} · Closed ${data.jobs_in_play.managed.closed} · Archived ${data.jobs_in_play.managed.archived}`}
                  icon={BriefcaseBusiness}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Marketplace active"
                  value={String(data.jobs_in_play.marketplace.active)}
                  description={`${data.jobs_in_play.marketplace.expired} expired`}
                  icon={Globe2}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="With applications"
                  value={String(data.jobs_in_play.published_with_applications)}
                  description="Published managed roles with ≥1 application"
                  icon={BriefcaseBusiness}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Stale live"
                  value={String(data.jobs_in_play.stale_published_no_application)}
                  description={`Published, no application for ${data.jobs_in_play.stale_days}d`}
                  icon={Timer}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
            </Section>

            <Section
              title="Fulfillment"
              description="Hires in period and median days from publish to hire. No closed_at proxy."
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Hires"
                  value={String(data.fulfillment.hires_in_period)}
                  description={`In the last ${data.period.label}`}
                  icon={Timer}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Median publish→hire"
                  value={formatDays(data.fulfillment.median_days_publish_to_hire)}
                  description={
                    data.fulfillment.hire_samples_for_median
                      ? `${data.fulfillment.hire_samples_for_median} hire(s) with published_at`
                      : 'No hire samples with published_at'
                  }
                  icon={Timer}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Pipeline depth"
                  value={String(
                    data.fulfillment.pipeline.applied
                      + data.fulfillment.pipeline.in_review
                      + data.fulfillment.pipeline.interview
                      + data.fulfillment.pipeline.offer,
                  )}
                  description={`Applied ${data.fulfillment.pipeline.applied} · Review ${data.fulfillment.pipeline.in_review} · Interview ${data.fulfillment.pipeline.interview} · Offer ${data.fulfillment.pipeline.offer}`}
                  icon={Activity}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
            </Section>

            <Section
              title="Marketplace interest"
              description="Candidate interest before outbound Apply — counts by board, recent rows with truncated notes."
              link={{
                to: data.marketplace_interests.list_path,
                label: 'Browse all interests',
              }}
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Interests"
                  value={String(data.marketplace_interests.total)}
                  description={`In the last ${data.period.label}`}
                  icon={Heart}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="With notes"
                  value={String(data.marketplace_interests.with_note)}
                  description={`${data.marketplace_interests.blank_note} without a note`}
                  icon={Heart}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Boards"
                  value={String(Object.keys(data.marketplace_interests.by_provider).length)}
                  description={providerBreakdown(data.marketplace_interests.by_provider)}
                  icon={Globe2}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
              {data.marketplace_interests.recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No marketplace interest recorded in this period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Board</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.marketplace_interests.recent.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="align-top">
                            <div className="font-medium">{row.candidate_name}</div>
                            {row.candidate_email ? (
                              <div className="text-xs text-muted-foreground">
                                {row.candidate_email}
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell className="align-top">
                            <Link
                              to={row.job_path}
                              className="text-primary underline-offset-4 hover:underline"
                            >
                              {row.job_title}
                            </Link>
                          </TableCell>
                          <TableCell className="align-top capitalize">{row.provider}</TableCell>
                          <TableCell className="align-top tabular-nums text-muted-foreground">
                            {new Date(row.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="align-top text-sm">
                            <TruncatedNote note={row.note} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Section>

            <Section
              title="Marketplace ingest"
              description="Last sync health and active vs expired listing counts."
              link={{ to: data.ingest.admin_path, label: 'Marketplace ingest' }}
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Last sync"
                  value={data.ingest.last_sync_status || '—'}
                  description={
                    data.ingest.last_error
                      ? data.ingest.last_error
                      : data.ingest.last_sync_at
                        ? new Date(data.ingest.last_sync_at).toLocaleString()
                        : 'No sync recorded'
                  }
                  icon={Globe2}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Failing terms"
                  value={String(data.ingest.failing_terms)}
                  description={
                    data.ingest.last_error
                      ? `${data.ingest.enabled_terms} enabled of ${data.ingest.total_terms} · ${data.ingest.last_error}`
                      : `${data.ingest.enabled_terms} enabled of ${data.ingest.total_terms}`
                  }
                  icon={Globe2}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Active listings"
                  value={String(data.ingest.marketplace_active)}
                  description={`${data.ingest.marketplace_expired} expired`}
                  icon={Globe2}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
            </Section>

            <Section
              title="Activity"
              description="Thin rollup for the selected period. Full detail lives on Engagement."
              link={{ to: data.activity.engagement_path, label: 'Engagement' }}
            >
              <DashboardMetricGrid maxColumns={4}>
                <DashboardMetricCard
                  title="Signups"
                  value={String(data.activity.signups)}
                  icon={Activity}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Recent logins"
                  value={String(data.activity.recent_logins)}
                  icon={Activity}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Applications"
                  value={String(data.activity.applications)}
                  icon={Activity}
                  loading={isLoading}
                />
                <DashboardMetricCard
                  title="Newly published"
                  value={String(data.activity.managed_published)}
                  description="Managed roles published in period"
                  icon={BriefcaseBusiness}
                  loading={isLoading}
                />
              </DashboardMetricGrid>
            </Section>

            <Section
              title="Current rubrics"
              description="Tenant evaluation corpora and their current revision versions."
              link={{ to: data.rubrics.admin_path, label: 'Evaluation rubrics' }}
            >
              {data.rubrics.corpora.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tenant rubric corpora found.</p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {data.rubrics.corpora.map((corpus) => (
                    <li
                      key={corpus.id}
                      className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm"
                    >
                      <span className="inline-flex items-center gap-2">
                        <ClipboardList className="size-4 text-muted-foreground" aria-hidden />
                        <span className="font-medium">{corpus.name}</span>
                        <span className="text-muted-foreground">{corpus.slug}</span>
                        {corpus.is_tenant_default ? (
                          <span className="text-xs text-muted-foreground">default</span>
                        ) : null}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {corpus.current_version != null
                          ? `v${corpus.current_version}`
                          : 'no revision'}{' '}
                        · {corpus.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </>
        ) : null}
      </PulseStatus>
    </PageContainer>
  )
}
